import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ReportBreakdownItem, ReportPeriodPoint } from '@/types/report';
import { formatInr } from '@/utils/currency';
import { CHART_COLORS } from './reportColumns';
import styles from './Reports.module.scss';

interface TrendChartProps {
  title: string;
  data: ReportPeriodPoint[];
  valueLabel?: string;
  currency?: boolean;
}

export function TrendLineChart({ title, data = [], valueLabel = 'Value', currency = false }: TrendChartProps) {
  const chartData = data.map((point) => ({
    label: point.label,
    value: point.value,
    orders: point.orders ?? 0,
  }));

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartTitle}>{title}</div>
      <div className={styles.chartBody}>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(17, 80, 153, 0.12)" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#5A5A62' }} />
            <YAxis tick={{ fontSize: 11, fill: '#5A5A62' }} />
            <Tooltip
              formatter={(value) => (currency ? formatInr(Number(value ?? 0)) : Number(value ?? 0))}
              contentStyle={{
                borderRadius: 10,
                border: '1px solid #E5E4DF',
                boxShadow: '0 8px 24px rgba(14, 14, 19, 0.08)',
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="value"
              name={valueLabel}
              stroke="#115099"
              strokeWidth={3}
              dot={{ r: 3, fill: '#115099' }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

interface BreakdownChartProps {
  title: string;
  data: ReportBreakdownItem[];
  currency?: boolean;
}

export function BreakdownBarChart({ title, data = [], currency = true }: BreakdownChartProps) {
  const chartData = data.map((item) => ({
    label: item.label,
    value: item.value,
    count: item.count,
  }));

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartTitle}>{title}</div>
      <div className={styles.chartBody}>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(17, 80, 153, 0.12)" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#5A5A62' }} />
            <YAxis tick={{ fontSize: 11, fill: '#5A5A62' }} />
            <Tooltip
              formatter={(value) => (currency ? formatInr(Number(value ?? 0)) : Number(value ?? 0))}
              contentStyle={{
                borderRadius: 10,
                border: '1px solid #E5E4DF',
              }}
            />
            <Bar dataKey="value" name="Value" radius={[8, 8, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={entry.label} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function BreakdownPieChart({ title, data = [] }: BreakdownChartProps) {
  const chartData = data.map((item) => ({
    name: item.label,
    value: item.count || item.value,
  }));

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartTitle}>{title}</div>
      <div className={styles.chartBody}>
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={58}
              outerRadius={96}
              paddingAngle={3}
            >
              {chartData.map((entry, index) => (
                <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                borderRadius: 10,
                border: '1px solid #E8DFD4',
              }}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
